import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Matches from './matches/Matches';
import Teams from "../../teams/Teams";
import { Images } from "../../../../utils/imageConstants";
import BreadCrumb from "../../../../components/BreadCrumb";
import Gallery from "./gallery/Gallery";
import GalleryDetails from "./gallery/GalleryDetails";
import TournamentPlayersTab from "./TournamentPlayersTab";
import TournamentStatsTab from "./TournamentStatsTab";
import TournamentAboutTab from "./TournamentAboutTab";
import MyWaiverTab from "./waiver/MyWaiverTab";
import { formatDate } from "../../../../helper/helper";
import { QUERY_KEY } from "../../../../services/queryEndPoint";
import { Api } from "../../../../services/interceptor";
import type { ITournamentsByLeagueResponse } from "../../types/home";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  selectSelectedLeague,
  setSelectedLeague,
} from "../../../../redux/slice/selectedLeagueSlice";
import {
  fetchAllLeagues,
  mapLeagueItemToGetAllLeagues,
} from "../../../myLeagues/api/myLeaguesApi";
import { buildMinimalLeagueFromWebSearch } from "../../../../utils/websearchParse";

type TabKey = "matches" | "teams" | "stats" | "gallery" | "about" | "player" | "waiver";

interface GalleryItem {
  match: string;
  date: string;
  photoCount: number;
  matchId?: string;
  imageUrl?: string;
  images?: string[];
}

interface TournamentDirectFetch {
  tournamentId: string;
  tournamentName: string | null;
  leagueId: string | null;
  leagueName: string | null;
}

const TOURNAMENT_TABS: { key: TabKey; label: string }[] = [
  { key: "matches", label: "Matches" },
  { key: "teams", label: "Teams" },
  { key: "stats", label: "Stats" },
  { key: "gallery", label: "Gallery" },
  { key: "about", label: "About" },
  { key: "waiver", label: "My Waiver" },
  // { key: "player", label: "Player" },
];

const TournamentsDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tournamentId: urlTournamentId, leagueId: urlLeagueId } = useParams<{
    tournamentId?: string;
    leagueId?: string;
  }>();
  const [activeTab, setActiveTab] = useState<TabKey>(() => {
    const requestedFromState = (location.state as { lastTournamentTab?: TabKey } | null)?.lastTournamentTab;
    // `?tab=waiver` lets an external link (e.g. the waiver reminder email) deep-link straight into
    // a tab — location.state only survives an internal navigate() call, not a fresh page load.
    const requestedFromQuery = new URLSearchParams(location.search).get("tab") as TabKey | null;
    const requested = requestedFromState ?? requestedFromQuery;
    return requested && TOURNAMENT_TABS.some((t) => t.key === requested) ? requested : "matches";
  });
  const tournamentInfo = location.pathname.includes("/tournaments/team-info");

  // Get tournamentId from URL params or sessionStorage
  const tournamentId = urlTournamentId || sessionStorage.getItem("tournamentId");
  // Hydrate selectedLeague when entering tournament routes that bypass LeagueRouteHydrator.
  // Without this, permission checks like canScheduleMatchesInLeague return false and
  // actions such as "Schedule Match" are hidden. We also rehydrate when the current
  // selectedLeague is the minimal placeholder shape produced by global search
  // (buildMinimalLeagueFromWebSearch), which has empty role and zero permissions.
  const dispatch = useAppDispatch();
  const selectedLeague = useAppSelector(selectSelectedLeague);

  // Only fires once every cheaper source (URL param, sessionStorage, Redux) has already come up
  // empty — a single direct-by-id lookup that works with zero prior context, purely to recover
  // leagueId/tournamentName for a cold landing. See the comment on effectiveLeagueId below.
  const needsDirectTournamentFetch =
    !!tournamentId && !urlLeagueId && !sessionStorage.getItem("leagueId") && !selectedLeague?.leagueId;
  const { data: tournamentDirect } = useQuery<TournamentDirectFetch>({
    queryKey: [QUERY_KEY.addTournament, tournamentId, "direct"],
    queryFn: async () =>
      (await Api.get<TournamentDirectFetch>(`${QUERY_KEY.addTournament}/${tournamentId}`)).data,
    enabled: needsDirectTournamentFetch,
  });

  // Routes like /tournamentDetails/:tournamentId don't include leagueId — fall back
  // to sessionStorage, then to whatever league is currently in Redux, then finally to a direct
  // by-id tournament fetch (see tournamentDirect below) for a cold landing with none of the
  // above — e.g. a waiver reminder email link opened after a fresh login, where the user has
  // never navigated through this league's own pages in this browser/session at all. Without that
  // last fallback, effectiveLeagueId stays null forever: needsLeagueHydration below only ever
  // fires once effectiveLeagueId is already known, so nothing rehydrates selectedLeague, the
  // tournament's own name never resolves (tournamentsByLeague is also gated on effectiveLeagueId),
  // and every leagueId-dependent prop passed to a tab (MyWaiverTab's AdminWaiverPanel/wizard,
  // TournamentAboutTab) is permanently null.
  const effectiveLeagueId =
    urlLeagueId ||
    sessionStorage.getItem("leagueId") ||
    selectedLeague?.leagueId ||
    tournamentDirect?.leagueId ||
    null;
  const isPlaceholderLeague =
    !!selectedLeague &&
    !selectedLeague.roleName &&
    !selectedLeague.canEditLeague &&
    !selectedLeague.canAddTournamentToLeague &&
    !selectedLeague.canAddTeamToLeague &&
    !selectedLeague.canAddPlayerToTeam &&
    !selectedLeague.canCreateDivisions;
  const needsLeagueHydration =
    !!effectiveLeagueId &&
    (selectedLeague?.leagueId !== effectiveLeagueId || isPlaceholderLeague);
  const { data: allLeaguesForHydration } = useQuery({
    queryKey: ["myLeagues", "allLeague"],
    queryFn: fetchAllLeagues,
    enabled: needsLeagueHydration,
    staleTime: 30_000,
  });
  useEffect(() => {
    if (!needsLeagueHydration) return;

    if (allLeaguesForHydration) {
      const match = allLeaguesForHydration.find(
        (l) => l.leagueId === effectiveLeagueId,
      );
      if (match) {
        dispatch(setSelectedLeague(mapLeagueItemToGetAllLeagues(match)));
        return;
      }
    }

    // Reached when allLeaguesForHydration hasn't resolved yet, or resolved with no match for
    // this league — allLeaguesForHydration only covers leagues the player has some relationship
    // to beyond being on a roster (created/admins/joined), so a player who landed here purely via
    // a tournament deep link (e.g. the waiver reminder flow) may never appear there at all. Note
    // this is deliberately NOT gated on `allLeaguesForHydration` having actually resolved — a
    // *failed* fetch (401/403 for this caller) leaves its `data` undefined forever, same as
    // "still loading" from this effect's perspective, which would otherwise block this fallback
    // from ever running. Without it, selectedLeague stays permanently unset and every nav element
    // reading selectedLeague?.leagueName (SubHeader's league-switcher label, etc.) is stuck
    // showing "-". If allLeaguesForHydration later DOES resolve with a real match (e.g. a retry
    // succeeds), needsLeagueHydration stays true because a minimal placeholder is itself a
    // "placeholder" per isPlaceholderLeague above, so this effect re-runs and the branch above
    // upgrades it to the full record.
    if (selectedLeague?.leagueId === effectiveLeagueId) return; // already have something for this league — don't redispatch a fresh object reference every render
    if (effectiveLeagueId && tournamentDirect?.leagueId === effectiveLeagueId) {
      dispatch(setSelectedLeague(buildMinimalLeagueFromWebSearch(effectiveLeagueId, tournamentDirect.leagueName ?? "")));
    }
  }, [needsLeagueHydration, allLeaguesForHydration, effectiveLeagueId, tournamentDirect, selectedLeague, dispatch]);

  // Resolve tournament name: prefer live fetch (via leagueId), fall back to sessionStorage
  const { data: tournamentsByLeague } = useQuery<ITournamentsByLeagueResponse>({
    queryKey: [QUERY_KEY.getTournamentsByLeagues, effectiveLeagueId],
    enabled: !!effectiveLeagueId,
    queryFn: async () =>
      (
        await Api.get<ITournamentsByLeagueResponse>(
          `${QUERY_KEY.getTournamentsByLeagues}/${effectiveLeagueId}`,
        )
      ).data,
  });
  const fetchedName = tournamentsByLeague?.tournamentResponses?.find(
    (t) => t.tournamentId === tournamentId,
  )?.tournamentName;
  const tournamentName = fetchedName || tournamentDirect?.tournamentName || sessionStorage.getItem("tournamentName");
  useEffect(() => {
    if (fetchedName) {
      sessionStorage.setItem("tournamentName", fetchedName);
    }
  }, [fetchedName]);
  // Persist the recovered leagueId the same way tournamentName is above, so the rest of this
  // session (navigating between tabs, revisiting this tournament) benefits from the recovered
  // context too instead of re-running the direct fetch on every subsequent mount.
  useEffect(() => {
    if (tournamentDirect?.leagueId && tournamentDirect.leagueId !== sessionStorage.getItem("leagueId")) {
      sessionStorage.setItem("leagueId", tournamentDirect.leagueId);
    }
  }, [tournamentDirect?.leagueId]);
  const isGalleryDetails = location.pathname === "/tournaments/tournaments-details/gallery-details";
  const galleryItem = (location.state as { galleryItem?: GalleryItem })?.galleryItem;

  // Update sessionStorage when tournamentId comes from URL
  useEffect(() => {
    if (urlTournamentId && urlTournamentId !== sessionStorage.getItem("tournamentId")) {
      sessionStorage.setItem("tournamentId", urlTournamentId);
    }
  }, [urlTournamentId]);

  // Consume the requested tab once so it doesn't re-apply on later renders/back navigation
  useEffect(() => {
    const st = (location.state ?? {}) as { lastTournamentTab?: TabKey };
    if (st.lastTournamentTab) {
      const { lastTournamentTab, ...rest } = st;
      navigate(location.pathname + location.search, { state: rest, replace: true });
    }
  }, []);

  // Set active tab to gallery when on gallery-details route
  useEffect(() => {
    if (isGalleryDetails) {
      setActiveTab("gallery");
    }
  }, [isGalleryDetails]);

  // Store tournament tab in sessionStorage when it changes and dispatch custom event
  useEffect(() => {
    if (activeTab) {
      const tabLabel = TOURNAMENT_TABS.find(t => t.key === activeTab)?.label || activeTab;
      sessionStorage.setItem("tournamentTab", tabLabel);
      // Dispatch custom event to notify BreadCrumb of the change
      window.dispatchEvent(new Event("tournamentTabChanged"));
    }
  }, [activeTab]);

  const handleTabClick = (tabKey: TabKey) => {
    setActiveTab(tabKey);
    // If on gallery-details page and clicking any tab, navigate back to tournaments-details
    if (isGalleryDetails) {
      navigate("/tournaments/tournaments-details", { replace: true });
    }
  };

  const renderTabContent = () => {
    if (isGalleryDetails) {
      return <GalleryDetails />;
    }
    if (activeTab === "matches") return <Matches />;
    if (activeTab === "teams") return <Teams />;
    if (activeTab === "gallery") return <Gallery />;
    if (activeTab === "player") return <TournamentPlayersTab />;
    if (activeTab === "waiver")
      return (
        <MyWaiverTab
          tournamentId={tournamentId}
          tournamentName={tournamentName}
          leagueId={effectiveLeagueId}
        />
      );
    if (activeTab === "stats") return <TournamentStatsTab tournamentId={tournamentId} />;
    if (activeTab === "about")
      return (
        <TournamentAboutTab
          tournamentId={tournamentId}
          leagueId={effectiveLeagueId}
        />
      );
    return (
      <div className="no-data-found">
        <img src={Images.my_leagues_no} alt="No data" />
        <p>No Data found</p>
      </div>
    );
  };

  return (
    <div className={!tournamentInfo ? "main-match-wrapper tournament-details-page" : ""}>
      <div className={!tournamentInfo ? "custom-container main-middle-content-wrap" : ""}>
        {!tournamentInfo &&
          <BreadCrumb />
        }
        <div className={!tournamentInfo ? "match-bg-wrapper" : ""}>
          {!tournamentInfo && !isGalleryDetails &&
            <div className="images-name-wrapper">
              <p className="match-name">
                {/* Keep trailing year ranges (e.g. "2026-27") from breaking across lines */}
                {(tournamentName || "-").replace(/(\d{4})-(\d{2,4})\b/g, "$1‑$2")}
              </p>
            </div>
          }
          {!tournamentInfo && isGalleryDetails && galleryItem &&
            <div className="gallery-details-header-wrapper">
              <div className="gallery-details-header-content">
                <img
                  src={Images.white_back_arrow}
                  alt="Back"
                  className="gallery-details-back-button"
                  onClick={() => navigate(-1)}
                />
                <div className="gallery-details-match-info">
                  <p className="gallery-details-match-title">{galleryItem.match}</p>
                  <p className="gallery-details-match-meta">
                    {formatDate(galleryItem.date)} | {galleryItem.photoCount} Photos
                  </p>
                </div>
              </div>
            </div>
          }
          <div className={!tournamentInfo ? "matches-wrapper-container" : "team-info-matches-wrapper-container"}>

            <div className="tournament-details">
              <nav className="header-tabs" aria-label="Tournament tabs">
                {TOURNAMENT_TABS.map((t) => {
                  const isActive = activeTab === t.key;
                  return (
                    <a
                      key={t.key}
                      href="#"
                      className={isActive ? "active" : "inactive-tab"}
                      onClick={(e) => {
                        e.preventDefault();
                        handleTabClick(t?.key);
                      }}
                    >
                      {t.label}
                    </a>
                  );
                })}
              </nav>

              <div className="tab-content">
                {renderTabContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>


  );
};

export default TournamentsDetails;

